import { writeFile } from "node:fs/promises";
import path from "node:path";
import { z } from "zod";
import type { ReviewAgent, ReviewInput } from "./agent.js";
import { runClaudeAgent } from "./agent-process.js";
import { logger } from "./log.js";
import { resolvePrompts } from "./prompt.js";
import { reviewResultSchema, toClaudeJsonSchema, type ReviewResult } from "./schema.js";
import type { ClaudeCodeModel } from "./config.js";

const log = logger.child("reviewer");

const envelopeSchema = z.object({
  is_error: z.boolean().optional(),
  result: z.string().optional(),
  structured_output: z.unknown().optional(),
}).loose();

export interface ClaudeCodeAgentOptions {
  executable?: string;
  playwrightMcpPackage?: string;
  model?: ClaudeCodeModel;
}

export class ClaudeCodeAgent implements ReviewAgent {
  constructor(private readonly options: ClaudeCodeAgentOptions = {}) {}

  async review(input: ReviewInput): Promise<ReviewResult> {
    const mcpPath = path.join(input.runtimeDirectory, "mcp.json");
    const settingsPath = path.join(input.runtimeDirectory, "claude-settings.json");
    const rawOutputPath = path.join(input.runtimeDirectory, "claude-output.json");

    await Promise.all([
      writeFile(mcpPath, JSON.stringify({
        mcpServers: {
          playwright: {
            command: "pnpm",
            args: [
              "dlx",
              this.options.playwrightMcpPackage ?? "@playwright/mcp@0.0.45",
              "--headless",
              "--browser",
              "chromium",
              "--output-dir",
              input.evidenceDirectory,
            ],
          },
        },
      }, null, 2)),
      writeFile(settingsPath, JSON.stringify({
        permissions: {
          allow: ["Read", "Glob", "Grep", "Bash(git diff *)", "Bash(git status *)", "mcp__playwright__*"],
          deny: ["Write", "Edit", "Bash(git push *)", "Bash(git commit *)", "Bash(rm *)", "WebFetch", "WebSearch"],
        },
      }, null, 2)),
    ]);

    const prompts = await resolvePrompts(input);
    const executable = this.options.executable ?? "claude";
    const args = [
      "-p", prompts.review,
      "--append-system-prompt", prompts.root,
      "--output-format", "stream-json",
      "--verbose",
      "--json-schema", toClaudeJsonSchema(reviewResultSchema),
      "--mcp-config", mcpPath,
      "--settings", settingsPath,
      "--permission-mode", "dontAsk",
    ];
    if (this.options.model) args.push("--model", this.options.model);

    const done = log.step(`Starting review agent (${executable}${this.options.model ? `, model ${this.options.model}` : ""})`);
    const { envelope: rawEnvelope, stdout, stderr, code, timedOut } = await runClaudeAgent({
      executable, args, cwd: input.cwd, timeoutMs: input.timeoutMs, label: "review agent", log,
    });
    await writeFile(rawOutputPath, stdout || stderr);

    if (timedOut) throw new Error(`Review agent timed out after ${input.timeoutMs}ms`);
    if (code !== 0) throw new Error(`Claude exited with code ${code}: ${stderr.trim()}`);
    if (rawEnvelope === undefined) throw new Error("Review agent produced no result");
    const envelope = envelopeSchema.parse(rawEnvelope);
    if (envelope.is_error) throw new Error(envelope.result ?? "Claude reported an execution error");
    const result = reviewResultSchema.parse(envelope.structured_output);
    done(`Review agent finished: ${result.status}, ${result.findings.length} finding(s)`);
    return result;
  }
}
